import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SubCustomerPermission } from '@airwallet/shared-models/sub-customer';
import { Details, OnboardingState } from '@airwallet/shared-models/details';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { CustomerService } from '../customer/customer.service';
import { lastValueFrom } from 'rxjs';
import { HomeService } from '@services/home/home.service';
import { HelperService } from '@services/helper/helper.service';
import { SubscriptionPlanInfo } from '@airwallet/shared-models/feature_access';
import { AccountService } from '@services/account/account.service';
@Injectable({
    providedIn: 'root'
})
export class OnboardingService {
    baseUrl: string = environment.baseUrl;
    user: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();

    customerDetails: Signal<Details> = this.homeService.customerDetails;
    subCustomerPermissions: WritableSignal<SubCustomerPermission> = signal({
        allow_billing: true,
        allow_location: true,
        allow_refund: true,
        allow_change_billing: true,
        allow_advanced_share: true,
        lock_advanced_share: false,
        allow_coupon: true
    });
    onboardingState: Signal<OnboardingState> = computed(() => {
        const customerDetails: Details = this.customerDetails();
        return customerDetails?.onboarding_state;
    });
    onboardingComplete: WritableSignal<boolean> = signal(false);
    isLife: Signal<boolean> = computed(() => {
        const customerDetails: Details = this.customerDetails();
        return customerDetails?.agreement_type === 'life';
    });
    noShare: Signal<boolean> = computed(() => {
        return this.subCustomerPermissions()?.no_share;
    });

    // Signals that indicate the current state of the onboarding process
    hasCompletedAccountInformation: Signal<boolean> = computed(() => {
        return this.onboardingState().external_account || this.noShare();
    });

    shouldShowKYC: Signal<boolean> = computed(() => {
        return !this.noShare() || !this.isLife();
    });

    shouldShowPlanAndBilling: Signal<boolean> = computed(() => {
        return this.isLife() && this.subCustomerPermissions()?.allow_billing;
    });

    hasCompletedPlan: Signal<boolean> = computed(() => {
        const planInfo: SubscriptionPlanInfo = this.accountService.currentSubscriptionPlanInfo();
        const subscriptionPlanIntervalIsSet: boolean = planInfo && planInfo.subscription_plan_interval !== 0;
        return (subscriptionPlanIntervalIsSet && this.hasCompletedKYC()) || (!this.shouldShowPlanAndBilling() && this.hasCompletedAccountInformation());
    });

    hasCompletedKYC: Signal<boolean> = computed(() => {
        return this.onboardingState()?.capabilities === 'active' || !this.shouldShowKYC();
    });

    nextStepIsBilling: Signal<boolean> = computed(() => {
        return this.hasCompletedPlan();
    });

    constructor(
        private http: HttpClient,
        private customerService: CustomerService,
        private homeService: HomeService,
        private helperService: HelperService,
        private accountService: AccountService
    ) {}

    async getSubcustomerPermissionsFromSub(): Promise<void> {
        return new Promise(resolve => {
            const subCustomerPermissionSub = this.customerService.readSubCustomerPermission(this.user.uid).subscribe(query => {
                subCustomerPermissionSub.unsubscribe();
                this.subCustomerPermissions.set(query.snapshot.val());
                return resolve();
            });
        });
    }

    async getOnboardingStatus(): Promise<void> {
        if (this.user.uid.includes('_operated_by_')) {
            await this.getSubcustomerPermissionsFromSub();
        }
        const { billing, capabilities, external_account } = this.customerDetails().onboarding_state;
        if (
            (billing && external_account && capabilities === 'active') ||
            (!this.isLife() && external_account && capabilities === 'active') ||
            (!this.subCustomerPermissions()?.allow_billing && external_account && capabilities === 'active') ||
            (this.subCustomerPermissions()?.allow_billing && this.subCustomerPermissions()?.no_share && billing) ||
            (!this.subCustomerPermissions()?.allow_billing && this.subCustomerPermissions()?.no_share)
        ) {
            this.onboardingComplete.set(true);
        } else {
            this.onboardingComplete.set(false);
        }
    }

    async getStripeKYCUrl(): Promise<string> {
        const stripeKYCUrl = '';
        await lastValueFrom(this.http.get(`${environment.baseUrl}/api_account/stripe_kyc_link?success_url=${window.location.origin + '/account'}&failure_url=${window.location.origin + '/account'}`))
            .then((res: any) => {
                window.open(res.url, '_self');
            })
            .catch(err => {
                console.error(err);
            });
        return stripeKYCUrl;
    }
}
